// Client-side sudoku engine — recreates the retired dvsml.com GraphQL backend's
// `newSudoku` / `checkSudoku` resolvers so the game runs with no server.

const SIZE = 9
const BOX = 3
const NUMS = [0, 1, 2, 3, 4, 5, 6, 7, 8]

const shuffle = arr => {
  const a = arr.slice()
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1))
    ;[a[i], a[j]] = [a[j], a[i]]
  }
  return a
}

export const squareOf = (row, col) => Math.floor(row / BOX) * BOX + Math.floor(col / BOX)

const hasValue = v => v !== null && v !== undefined

const isValidPlacement = (board, row, col, n) => {
  for (let i = 0; i < SIZE; i++) {
    if (board[row][i] === n || board[i][col] === n) return false
  }
  const sr = Math.floor(row / BOX) * BOX
  const sc = Math.floor(col / BOX) * BOX
  for (let r = sr; r < sr + BOX; r++) {
    for (let c = sc; c < sc + BOX; c++) {
      if (board[r][c] === n) return false
    }
  }
  return true
}

// Backtracking solver (fills a board in place). Returns true if solved.
const solve = board => {
  for (let i = 0; i < SIZE * SIZE; i++) {
    const r = Math.floor(i / SIZE)
    const c = i % SIZE
    if (board[r][c] === null) {
      for (const n of shuffle(NUMS)) {
        if (isValidPlacement(board, r, c, n)) {
          board[r][c] = n
          if (solve(board)) return true
          board[r][c] = null
        }
      }
      return false
    }
  }
  return true
}

const empty2d = () => Array.from({ length: SIZE }, () => Array(SIZE).fill(null))

// flat81 -> 9x9 array of cell objects (matches the GraphQL `puzzleFormatted` shape)
export const formatBoard = flat => {
  const rows = []
  for (let row = 0; row < SIZE; row++) {
    const cells = []
    for (let col = 0; col < SIZE; col++) {
      const index = row * SIZE + col
      cells.push({
        key: `${row}-${col}`,
        index,
        column: col,
        row,
        square: squareOf(row, col),
        squareRowIndex: row % BOX,
        squareColIndex: col % BOX,
        display: hasValue(flat[index]) ? flat[index] + 1 : null
      })
    }
    rows.push(cells)
  }
  return rows
}

// For each digit 1-9, which rows/columns/squares still need it (hint targets).
// Puzzle values are 0-based (0 = digit 1); display digits are 1-based.
export const computeHints = flat => {
  const hints = []
  for (let n = 1; n <= SIZE; n++) {
    const value = n - 1
    const rows = []
    const columns = []
    const squares = []

    for (let r = 0; r < SIZE; r++) {
      let present = false
      for (let c = 0; c < SIZE; c++) {
        if (flat[r * SIZE + c] === value) { present = true; break }
      }
      if (!present) rows.push(r)
    }
    for (let c = 0; c < SIZE; c++) {
      let present = false
      for (let r = 0; r < SIZE; r++) {
        if (flat[r * SIZE + c] === value) { present = true; break }
      }
      if (!present) columns.push(c)
    }
    for (let s = 0; s < SIZE; s++) {
      const sr = Math.floor(s / BOX) * BOX
      const sc = (s % BOX) * BOX
      let present = false
      for (let r = sr; r < sr + BOX; r++) {
        for (let c = sc; c < sc + BOX; c++) {
          if (flat[r * SIZE + c] === value) { present = true; break }
        }
      }
      if (!present) squares.push(s)
    }

    hints.push({ display: n, rows, columns, squares })
  }
  return hints
}

const hasDuplicates = cells => {
  const seen = new Set()
  for (const v of cells) {
    if (!hasValue(v)) continue
    if (seen.has(v)) return true
    seen.add(v)
  }
  return false
}

export const hasConflicts = flat => {
  for (let r = 0; r < SIZE; r++) {
    if (hasDuplicates(Array.from({ length: SIZE }, (_, c) => flat[r * SIZE + c]))) return true
  }
  for (let c = 0; c < SIZE; c++) {
    if (hasDuplicates(Array.from({ length: SIZE }, (_, r) => flat[r * SIZE + c]))) return true
  }
  for (let s = 0; s < SIZE; s++) {
    const sr = Math.floor(s / BOX) * BOX
    const sc = (s % BOX) * BOX
    const cells = []
    for (let r = sr; r < sr + BOX; r++) {
      for (let c = sc; c < sc + BOX; c++) {
        cells.push(flat[r * SIZE + c])
      }
    }
    if (hasDuplicates(cells)) return true
  }
  return false
}

export const isComplete = flat => flat.every(hasValue)

// 🔴 WHAT IS IN THE WAY — the reason a digit cannot go in a cell, in the order a player would
// look for it: the row, then the column, then the 3x3 box. It is here, in the engine, so the
// game and its tests read the SAME function and cannot disagree about why a move was refused.
//
// It was written to answer George's question of 22 September 2026 — "how can 7 not go there?
// its the only available space according to sudoku rules. is the board wrong?" — by making the
// site say where the other one is instead of only refusing.
//
// `index` itself is skipped, so it can be asked about a board the digit has already been put
// into. Returns null when nothing holds it.
export const findClash = (flat, index, value) => {
  const row = Math.floor(index / SIZE)
  const col = index % SIZE

  for (let c = 0; c < SIZE; c++) {
    const at = row * SIZE + c
    if (at !== index && flat[at] === value) return { where: 'row', number: row + 1 }
  }

  for (let r = 0; r < SIZE; r++) {
    const at = r * SIZE + col
    if (at !== index && flat[at] === value) return { where: 'column', number: col + 1 }
  }

  const sr = Math.floor(row / BOX) * BOX
  const sc = Math.floor(col / BOX) * BOX
  for (let r = sr; r < sr + BOX; r++) {
    for (let c = sc; c < sc + BOX; c++) {
      const at = r * SIZE + c
      if (at !== index && flat[at] === value) return { where: 'box' }
    }
  }

  return null
}

// The same answer in words, for the player. `digit` is the 1-based number as displayed.
export const describeClash = (clash, digit) => {
  if (!clash) return null
  if (clash.where === 'row') return `Already a ${digit} in row ${clash.number}.`
  if (clash.where === 'column') return `Already a ${digit} in column ${clash.number}.`
  return `Already a ${digit} in this 3 by 3 box.`
}

// newSudoku(): generate a solved grid, blank some cells to make the puzzle.
export const generateSudoku = (clues = 36) => {
  const solvedBoard = empty2d()
  solve(solvedBoard)
  const solved = solvedBoard.flat()

  const puzzle = solved.slice()
  let toRemove = SIZE * SIZE - clues
  for (const i of shuffle([...Array(SIZE * SIZE).keys()])) {
    if (toRemove <= 0) break
    puzzle[i] = null
    toRemove--
  }

  return {
    puzzle,
    solved,
    puzzleFormatted: formatBoard(puzzle),
    solvedFormatted: formatBoard(solved),
    hints: computeHints(puzzle)
  }
}

// checkSudoku(): validate the current state (no conflicts) and return hints + formatted board.
export const checkSudoku = (puzzle, userSubmitted = true) => {
  const flat = Array.isArray(puzzle) ? puzzle : []
  return {
    correct: !hasConflicts(flat),
    userSubmitted,
    puzzle: flat,
    puzzleFormatted: formatBoard(flat),
    hints: computeHints(flat)
  }
}
